import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import AuthLayout from '../components/AuthLayout';
import serviceRequestService, { ServiceRequestType, ServiceRequestFilters } from '../services/serviceRequest.service';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Constantes para labels
const priorityLabels = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  critical: 'Crítica'
};

const priorityColors = {
  low: 'success',
  medium: 'info',
  high: 'warning',
  critical: 'error'
};

const statusLabels = {
  waiting: 'Aguardando',
  in_progress: 'Em Execução',
  completed: 'Finalizado',
  cancelled: 'Cancelado'
};

const statusColors = {
  waiting: 'info',
  in_progress: 'warning',
  completed: 'success',
  cancelled: 'default'
};

const typeLabels = {
  maintenance: 'Manutenção',
  installation: 'Instalação',
  inspection: 'Inspeção',
  repair: 'Reparo',
  other: 'Outro'
};

const ServiceRequestList: React.FC = () => {
  const navigate = useNavigate();
  const [serviceRequests, setServiceRequests] = useState<ServiceRequestType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalServiceRequests, setTotalServiceRequests] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<ServiceRequestFilters>({});
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  // Função para buscar os chamados técnicos
  const fetchServiceRequests = async (params: ServiceRequestFilters = {}) => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Buscando chamados técnicos com parâmetros:', {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm,
        ...filters,
        ...params
      });
      
      const response = await serviceRequestService.getServiceRequests({
        page: page + 1, // API usa indexação baseada em 1
        limit: rowsPerPage,
        search: searchTerm,
        ...filters,
        ...params
      });
      
      console.log('Resposta da API:', response);
      console.log('Chamados recebidos:', response.docs);
      console.log('Total de chamados:', response.totalDocs);
      
      setServiceRequests(response.docs);
      setTotalServiceRequests(response.totalDocs);
    } catch (err: any) {
      console.error('Erro ao buscar chamados técnicos:', err);
      setError('Falha ao carregar chamados técnicos. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Carregar chamados técnicos ao montar o componente e quando os filtros mudarem
  useEffect(() => {
    fetchServiceRequests();
  }, [page, rowsPerPage, searchTerm, filters]);

  // Manipuladores de eventos para paginação
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Manipulador para busca
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  // Manipulador para abrir o diálogo de filtros
  const handleOpenFilterDialog = () => {
    setFilterDialogOpen(true);
  };

  // Manipulador para fechar o diálogo de filtros
  const handleCloseFilterDialog = () => {
    setFilterDialogOpen(false);
  };

  // Manipulador para aplicar filtros
  const handleApplyFilters = (newFilters: ServiceRequestFilters) => {
    setFilters(newFilters);
    setFilterDialogOpen(false);
    setPage(0);
  };

  // Manipulador para limpar filtros
  const handleClearFilters = () => {
    setFilters({});
    setFilterDialogOpen(false);
    setPage(0);
  };

  // Manipulador para atualizar a lista
  const handleRefresh = () => {
    fetchServiceRequests();
  };

  // Manipulador para criar um novo chamado técnico
  const handleCreateServiceRequest = () => {
    navigate('/service-requests/new');
  };

  // Manipulador para visualizar um chamado técnico
  const handleViewServiceRequest = (id: string) => {
    navigate(`/service-requests/${id}`);
  };

  // Manipulador para editar um chamado técnico
  const handleEditServiceRequest = (id: string) => {
    navigate(`/service-requests/edit/${id}`);
  };

  // Componente para o diálogo de filtros
  const FilterDialog = () => {
    const [localFilters, setLocalFilters] = useState<ServiceRequestFilters>({...filters});

    const handleFilterChange = (field: string, value: any) => {
      setLocalFilters(prev => ({
        ...prev,
        [field]: value
      }));
    };

    return (
      <Dialog open={filterDialogOpen} onClose={handleCloseFilterDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Filtrar Chamados Técnicos</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={localFilters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="waiting">Aguardando</MenuItem>
                <MenuItem value="in_progress">Em Execução</MenuItem>
                <MenuItem value="completed">Finalizado</MenuItem>
                <MenuItem value="cancelled">Cancelado</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel id="priority-filter-label">Prioridade</InputLabel>
              <Select
                labelId="priority-filter-label"
                value={localFilters.priority || ''}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                label="Prioridade"
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="low">Baixa</MenuItem>
                <MenuItem value="medium">Média</MenuItem>
                <MenuItem value="high">Alta</MenuItem>
                <MenuItem value="critical">Crítica</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel id="type-filter-label">Tipo</InputLabel>
              <Select
                labelId="type-filter-label"
                value={localFilters.type || ''}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                label="Tipo"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="maintenance">Manutenção</MenuItem>
                <MenuItem value="installation">Instalação</MenuItem>
                <MenuItem value="inspection">Inspeção</MenuItem>
                <MenuItem value="repair">Reparo</MenuItem>
                <MenuItem value="other">Outro</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearFilters}>Limpar Filtros</Button>
          <Button onClick={handleCloseFilterDialog}>Cancelar</Button>
          <Button 
            onClick={() => handleApplyFilters(localFilters)} 
            variant="contained" 
            color="primary"
          >
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <AuthLayout title="Chamados Técnicos">
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 500 }}>
            Chamados Técnicos
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Atualizar">
              <IconButton onClick={handleRefresh} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateServiceRequest}
              sx={{ 
                backgroundColor: '#2e7d32',
                '&:hover': {
                  backgroundColor: '#1b5e20',
                }
              }}
            >
              Novo Chamado
            </Button>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: { xs: 1, sm: 2 }, mb: 3 }}>
          <TextField
            label="Buscar"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ flexGrow: 1, minWidth: '200px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={handleOpenFilterDialog}
            size="small"
            sx={{ minWidth: '120px' }}
          >
            Filtros
            {Object.keys(filters).length > 0 && (
              <Chip 
                label={Object.keys(filters).length} 
                size="small" 
                color="primary" 
                sx={{ ml: 1, height: 20, width: 20 }}
              />
            )}
          </Button>
        </Box>
        
        {error && (
          <Box sx={{ mb: 3 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}
        
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: { xs: 'calc(100vh - 250px)', sm: 'calc(100vh - 300px)' }, overflowX: 'auto' }}>
            <Table stickyHeader aria-label="sticky table" size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Número</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>Ativo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', sm: 'table-cell' } }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>Prioridade</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', lg: 'table-cell' } }}>Responsável</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', lg: 'table-cell' } }}>Data</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={40} sx={{ color: '#2e7d32' }} />
                    </TableCell>
                  </TableRow>
                ) : serviceRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        Nenhum chamado técnico encontrado
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  serviceRequests.map((serviceRequest) => {
                    // Extrair informações do cliente e ativo
                    const clientName = typeof serviceRequest.client === 'object' && serviceRequest.client !== null
                      ? serviceRequest.client.name 
                      : 'Cliente não especificado';
                    
                    const assetName = typeof serviceRequest.asset === 'object' && serviceRequest.asset !== null
                      ? serviceRequest.asset.name 
                      : 'Ativo não especificado';
                    
                    const technicianName = typeof serviceRequest.assignedTo === 'object' && serviceRequest.assignedTo !== null
                      ? serviceRequest.assignedTo.displayName 
                      : 'Não atribuído';
                    
                    return (
                      <TableRow hover key={serviceRequest._id}>
                        <TableCell>{serviceRequest.number}</TableCell>
                        <TableCell>{clientName}</TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{assetName}</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          {typeLabels[serviceRequest.type as keyof typeof typeLabels] || serviceRequest.type}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={statusLabels[serviceRequest.status as keyof typeof statusLabels] || serviceRequest.status} 
                            color={statusColors[serviceRequest.status as keyof typeof statusColors] as any || "default"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          <Chip 
                            label={priorityLabels[serviceRequest.priority as keyof typeof priorityLabels] || serviceRequest.priority} 
                            color={priorityColors[serviceRequest.priority as keyof typeof priorityColors] as any || "default"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{technicianName}</TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          {serviceRequest.createdAt ? format(new Date(serviceRequest.createdAt), 'dd/MM/yyyy', { locale: ptBR }) : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ p: { xs: 0.5, sm: 1 }, pr: { xs: 1, sm: 2 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: { xs: 0, sm: 0.5 } }}>
                            <Tooltip title="Visualizar">
                              <IconButton 
                                size="small" 
                                onClick={() => handleViewServiceRequest(serviceRequest._id as string)}
                                sx={{ color: '#2196f3', p: { xs: 0.5, sm: 1 } }}
                              >
                                <VisibilityIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar">
                              <IconButton 
                                size="small" 
                                onClick={() => handleEditServiceRequest(serviceRequest._id as string)}
                                sx={{ color: '#ff9800', p: { xs: 0.5, sm: 1 } }}
                              >
                                <EditIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalServiceRequests}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </Paper>
      </Box>
      
      <FilterDialog />
    </AuthLayout>
  );
};

export default ServiceRequestList;
